import * as React from "react";
import { cn } from "@/lib/utils";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { InfoIcon, X } from "lucide-react";
import { href } from "react-router-dom";

export interface FloatingSelectOption {
	value: string;
	label: string;
}

export interface FloatingSelectProps {
	label: string;
	error?: string;
	value?: string;
	onValueChange?: (value: string) => void;
	disabled?: boolean;
	children?: React.ReactNode;
	placeholder?: string;
	className?: string;
	tabIndex?: number;
	/** Pass options here to enable the native <select> used for iOS keyboard "Next" support */
	options?: FloatingSelectOption[];
}

const FloatingSelect = React.forwardRef<HTMLButtonElement, FloatingSelectProps>(
	(
		{
			label,
			error,
			value,
			onValueChange,
			disabled,
			children,
			placeholder,
			className,
			tabIndex,
			options,
		},
		ref
	) => {
		const hasValue = !!value;

		/*
		 * iOS Safari "Next" only advances focus to <input>/<select>/<textarea>.
		 * When `options` are provided we render a native <select> on top (opacity-0,
		 * full-size) so iOS can focus it via "Next", while the styled Radix trigger
		 * remains visible underneath for desktop users.
		 */
		const nativeSelectRef = React.useRef<HTMLSelectElement>(null);

		return (
			<div className='relative'>
				{options && (
					<select
						ref={nativeSelectRef}
						disabled={disabled}
						tabIndex={tabIndex}
						value={value ?? ""}
						aria-label={label}
						onChange={(e) => {
							onValueChange?.(e.target.value);
						}}
						className='absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0 md:hidden'
					>
						<option value='' disabled>
							{placeholder || label}
						</option>
						{options.map((opt) => (
							<option key={opt.value} value={opt.value}>
								{opt.label}
							</option>
						))}
					</select>
				)}

				<Select value={value} onValueChange={onValueChange} disabled={disabled}>
					<SelectTrigger
						ref={ref}
						tabIndex={tabIndex}
						className={cn(
							"h-12 w-full rounded-lg border border-gray-300 bg-white px-4 text-left text-sm transition-all duration-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500",
							error &&
								"border-red-500 focus:border-red-500 focus:ring-red-500/20",
							className
						)}
					>
						<SelectValue placeholder={placeholder || label} />
					</SelectTrigger>
					<SelectContent className='max-h-[300px] overflow-y-auto'>
						{children}
					</SelectContent>
				</Select>

				{hasValue && !disabled && (
					<button
						type='button'
						onClick={(e) => {
							e.stopPropagation();
							onValueChange?.("");
						}}
						className='absolute top-1/2 right-8 z-20 -translate-y-1/2 rounded-full p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600'
					>
						<X className='h-3.5 w-3.5' />
					</button>
				)}

				{label && label === "Select binding" && (
					<a
						href='https://www.e-arc.com/digital-printing/construction-jobsite-graphics/binding-services/'
						target='_blank'
						rel='noreferrer'
						onClick={(e) => e.stopPropagation()}
						className={cn(
							"absolute top-1/2 z-20 -translate-y-1/2 text-gray-400 hover:text-blue-500",
							hasValue && !disabled ? "right-14" : "right-10"
						)}
					>
						<InfoIcon className='h-5 w-5 text-red-500' />
					</a>
				)}

				<label
					className={cn(
						"pointer-events-none absolute top-1/2 left-3 w-[80%] -translate-y-1/2 bg-white px-1 text-sm text-gray-500 transition-all duration-200",
						hasValue && "top-0 -translate-y-1/2 text-xs text-blue-600",
						error && "top-0 -translate-y-1/2 text-red-500"
					)}
				>
					{label}
				</label>
				{error && <p className='mt-1 text-xs text-red-600'>{error}</p>}
			</div>
		);
	}
);

FloatingSelect.displayName = "FloatingSelect";

export { FloatingSelect };
export { SelectItem };
